import React, { useState, useMemo, useCallback } from 'react';

import classnames from 'classnames';

import CollapsibleCard from '../collapsible-card/collapsible-card';
import { UI_SEARCH_COLLAPSIBLE_CARD_LIST_NAMESPACE as namespace } from '../../constants';
import CollapsibleCardListPropTypes from './propTypes';
import { withComponentHandler } from '../../hocs/with-component-handler';

const getKey = (index) => `${namespace}--item--${index}`;

/**
 * @typedef {Object} CollapsibleCardItem
 * @property {string} title - The title text to display in the card header
 * @property {string} content - The HTML content to be displayed in the card
 */

/**
 * @typedef {Object} CollapsibleCardListProps
 * @property {string} [className=''] - Additional CSS class names to apply to the list container
 * @property {CollapsibleCardItem[]} items - Array of items to be rendered as collapsible cards
 */

/**
 * CollapsibleCardList component renders a list of collapsible cards.
 * It manages the expanded state of each card, ensuring only one card can be expanded at a time.
 * When a card is clicked, it toggles its expanded state while collapsing any previously expanded card.
 *
 * @param {CollapsibleCardListProps} props - The props for the component
 * @returns {JSX.Element} A div containing a list of CollapsibleCard components
 */

const CollapsibleCardList = ({ className = '', items }) => {
  const [expandedElementIndex, setExpandedElementIndex] = useState(null);

  const toggleExpanded = useCallback((index) => {
    setExpandedElementIndex((currentIndex) => (index === currentIndex ? null : index));
  }, []);

  const renderedCards = useMemo(
    () =>
      items.map((item, index) => (
        <CollapsibleCard
          key={getKey(index)}
          title={item?.title}
          content={item?.content}
          isExpanded={index === expandedElementIndex}
          onClick={() => toggleExpanded(index)}
        />
      )),
    [items, expandedElementIndex, toggleExpanded],
  );

  return <div className={classnames(namespace, className)}>{renderedCards}</div>;
};

CollapsibleCardList.propTypes = {
  ...CollapsibleCardListPropTypes,
};

export default withComponentHandler(CollapsibleCardList, { componentName: 'CollapsibleCardList' });
